import React, { FC, useMemo } from 'react';
import { cleanArray } from '../../../common/utils';
import { useSystemClassifiers } from '../../../configuration/useSystemClassifiers';
import { DataObjectDescriptionVersionStore } from '../../DataObjectDescriptionVersionStore';
import { AlgorithmElementFunctionType, DataObjectType } from '../../types';
import type { AlgorithmMappingQueryDtoType } from '../mappings/schema/types';
import { BlackString } from './coloredLabels/BlackString';
import { GreenString } from './coloredLabels/GreenString';
import { GreyString } from './coloredLabels/GreyString';
import { OrangeString } from './coloredLabels/OrangeString';
import { PurpleString } from './coloredLabels/PurpleString';

export interface AlgorithmConditionProps {
  condition: string;
  mappings: AlgorithmMappingQueryDtoType[];
}

export const AlgorithmCondition: FC<AlgorithmConditionProps> = (props) => {
  const { condition, mappings } = props;

  const { ALGORITHM_ELEMENT_FUNCTION } = useSystemClassifiers(
    'ALGORITHM_ELEMENT_FUNCTION',
  );

  const [dataObjectDescriptionVersion] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');

  const elementValues = useMemo(
    () => [
      ...new Set<string>(
        ALGORITHM_ELEMENT_FUNCTION.systemClassifierItems
          .filter((item) => {
            if (
              dataObjectDescriptionVersion?.dataObjectType ===
                DataObjectType.IN &&
              item.code === 'GET_SUBJECT_FIELD'
            )
              return false;
            return Boolean(item.parameters);
          })
          .map((item) => {
            try {
              return JSON.parse(item.parameters);
            } catch (err) {
              console.error(err);
            }
            return;
          })
          .filter((item) => item.type !== AlgorithmElementFunctionType.FUNCTION)
          .map((item) => item.value)
          .filter((item) => !['(', ')', '.', ','].includes(item)),
      ), //exclude some typographical symbols and punctuation marks for cleaner look
    ],
    [ALGORITHM_ELEMENT_FUNCTION],
  );

  const mappingAliases: string[] = useMemo(
    () => [
      ...new Set<string>(
        (mappings || []).map(
          (item: AlgorithmMappingQueryDtoType) => item.alias,
        ),
      ),
    ],
    [mappings],
  );

  const conditionValue = useMemo(
    () =>
      cleanArray(
        condition.split(/('.+?')/g).map((string, stringIndex) => {
          if (!string) {
            return null;
          }
          //Make all values in '' green
          if (string.match(/('.+?')/)) {
            return (
              <GreenString key={'green-' + stringIndex}>{string}</GreenString>
            );
          }
          return string
            .split(/(\[T\W\d\])/g)
            .map((substring, substringIndex) => {
              if (substring.match(/(\[T\W\d\])/)) {
                return (
                  <BlackString
                    key={'black-' + stringIndex + '-' + substringIndex}
                  >
                    {substring}
                  </BlackString>
                );
              }
              return substring.split(/(\w+|\W+)/g).map((item, index) => {
                //Add grey box around elements
                if (elementValues.includes(item.trim())) {
                  return (
                    <GreyString
                      key={
                        'grey-' +
                        stringIndex +
                        '-' +
                        substringIndex +
                        '-' +
                        index
                      }
                    >
                      {item}
                    </GreyString>
                  );
                }
                //Make all mapping aliases purple
                if (mappingAliases.includes(item.trim())) {
                  return (
                    <PurpleString
                      key={
                        'purple-' +
                        stringIndex +
                        '-' +
                        substringIndex +
                        '-' +
                        index
                      }
                    >
                      {item}
                    </PurpleString>
                  );
                }
                //Add grey box around elements
                if (!isNaN(Number(item))) {
                  return (
                    <OrangeString
                      key={
                        'orange-' +
                        stringIndex +
                        '-' +
                        substringIndex +
                        '-' +
                        index
                      }
                    >
                      {item}
                    </OrangeString>
                  );
                }
                return (
                  <BlackString
                    key={
                      'black-' +
                      stringIndex +
                      '-' +
                      substringIndex +
                      '-' +
                      index
                    }
                  >
                    {item}
                  </BlackString>
                );
              });
            });
        }),
      ),
    [condition, elementValues, mappingAliases],
  );

  if (!conditionValue.length) {
    return null;
  }

  return <span>{conditionValue}</span>;
};
